import {
  Box,
  Select,
  MenuItem,
  Typography,
  FormControl,
  SelectProps,
  FormHelperText,
} from "@mui/material";
import { useField } from "formik";

import {
  GetKeyValue,
  GetListText,
  GetListValue,
  MenuAttributes,
} from "./helper";
import styles from "./styles";
import { SelectCompProps } from "./type";
import DisplayError from "./displayError";

/**
 * Component for rendering a select input with label, options, and error handling.
 * @param onChange Function to handle change events.
 * @param name Name attribute for the select input.
 * @param placeholder Placeholder text for the select input.
 * @param searchCall Function for handling search calls (if applicable).
 * @param onScroll Function to handle scroll events in the menu (if applicable).
 * @param clearSearch Function to clear search input (if applicable).
 * @param onClose Function to handle close events (if applicable).
 * @param handleChange Function to handle change events.
 * @param label Label text for the select input.
 * @param options Array of options for the select input.
 * @param isSearch Boolean indicating if search functionality is enabled.
 * @param labelKey Key for accessing label text in option objects.
 * @param valKey Key for accessing value in option objects.
 * @param externalStyles Custom styles for the component.
 * @param isRequired Boolean indicating if the select input is required.
 * @param errortipPlacement Placement of error tooltip (e.g., left, right).
 * @param labelValue Value for the label (if applicable).
 * @param disabledArray Array of keys for disabled options.
 * @param searchValue Current search value (if applicable).
 * @param onClickItem Function to handle click events on options.
 * @param isComplaint Boolean indicating if the component is for complaints.
 * @param otherProps Additional props passed to the component.
 * @returns JSX element for the select input with label, options, and error handling.
 */
export default function SelectComp({
  onChange,
  name,
  placeholder,
  searchCall,
  onScroll,
  clearSearch,
  onClose,
  handleChange,
  label = "",
  options = [],
  isSearch = false,
  labelKey = "",
  valKey = "",
  externalStyles = {},
  isRequired = false,
  errortipPlacement = "left",
  labelValue = "",
  disabledArray = [],
  searchValue = "",
  onClickItem = () => {},
  isComplaint = false,
  isAccountPrimaryShow = false,
  ...otherProps
}: SelectCompProps & SelectProps) {
  const [field, meta] = useField(name);

  /**
   * Handles blur event on the select input.
   * @param evt Blur event object.
   */
  const blurHandler = (evt: any) => {
    field.onBlur(evt);
  };

  /* eslint-disable react/no-unstable-nested-components */

  // Attributes for the Select component
  const attributes: SelectProps = {
    ...field,
    ...otherProps,
    id: `${name}-select`,
    error: meta.touched && Boolean(meta.error),
    sx: styles.select,
    displayEmpty: true,
    onBlur: (evt: any) => blurHandler(evt),
    // onFocus: () => focusHandler(),
  };

  if (onClose) attributes.onClose = onClose;
  if (onChange) attributes.onChange = onChange;

  /**
   * Renders the selected value or placeholder text.
   * @param selected Selected value from the options.
   * @returns JSX element displaying selected value or placeholder text.
   */
  const RenderValue = (selected: string) => {
    if (selected && selected.length > 0) {
      const selectedItem = options.find(
        (item) => GetListValue(item, valKey) === selected,
      );
      return selectedItem ? GetListText(labelKey, selectedItem) : selected;
    }
    return <Typography className="selectPlaceholder">{placeholder}</Typography>;
  };

  return (
    <Box sx={[externalStyles]} className="single-select">
      <FormControl
        fullWidth
        sx={isComplaint ? styles.formControlBlue : styles.formControl}
      >
        <Typography component="div" sx={styles.label}>
          {`${label}${isRequired ? "*" : ""}`}
        </Typography>

        <Select
          MenuProps={
            onScroll
              ? MenuAttributes(onScroll)
              : {
                  PaperProps: {
                    style: styles.paper,
                    sx: styles.select,
                  },
                }
          }
          renderValue={(selected: any) => RenderValue(selected)}
          {...attributes}
        >
          {options?.map(
            (listItem: string | { [key: string]: any }, index: number) => {
              const key = GetKeyValue(listItem, index, valKey);
              return (
                <MenuItem
                  key={key}
                  value={GetListValue(listItem, valKey)}
                  autoFocus={index === 0}
                  sx={styles.menuItem}
                  disabled={disabledArray.includes(key)}
                  onClick={() => onClickItem(listItem)}
                >
                  {GetListText(labelKey, listItem, isAccountPrimaryShow)}
                </MenuItem>
              );
            },
          )}
        </Select>
      </FormControl>
      {meta.touched && Boolean(meta.error) && (
        <FormHelperText error sx={styles.errorTooltip}>
          <DisplayError meta={meta} />
        </FormHelperText>
      )}
    </Box>
  );
}
